
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, Plus, Search } from "lucide-react";
import VideoCard from "./VideoCard";
import { useAuth } from "@/hooks/useAuth";
import { Video } from "@/types/database";

const VideosTab = () => {
  const { toast } = useToast();
  const [videos, setVideos] = useState<Video[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedVideoId, setSelectedVideoId] = useState<string | null>(null);

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      setIsLoading(true);

      const { data, error } = await supabase
        .from("videos")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) {
        throw error;
      }

      // Convert the data to the appropriate Video type
      const typedVideos: Video[] = data?.map((item: any) => ({
        ...item,
        id: item.id.toString(),
        user_id: item.user_id ? item.user_id.toString() : "",
      })) || [];

      setVideos(typedVideos);
    } catch (error) {
      console.error("Error fetching videos:", error);
      toast({
        title: "Error fetching videos",
        description: "Failed to load videos. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteVideo = async () => {
    if (!selectedVideoId) return;

    try {
      const { error } = await supabase
        .from("videos")
        .delete()
        .eq("id", parseInt(selectedVideoId));

      if (error) {
        throw error;
      }

      setVideos(videos.filter(video => video.id !== selectedVideoId));
      setIsDeleteDialogOpen(false);

      toast({
        title: "Video deleted",
        description: "The video has been deleted successfully.",
      });
    } catch (error) {
      console.error("Error deleting video:", error);
      toast({
        title: "Error deleting video",
        description: "Failed to delete video. Please try again.",
        variant: "destructive",
      });
    }
  };

  const openDeleteDialog = (id: string) => {
    setSelectedVideoId(id);
    setIsDeleteDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Search videos..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="w-full py-20 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : videos.length === 0 ? (
        <div className="text-center py-20">
          {searchQuery ? (
            <p className="text-muted-foreground">No videos match your search query</p>
          ) : (
            <div className="space-y-4">
              <p className="text-muted-foreground">No videos found</p>
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {videos.map((video) => (
            <VideoCard
              key={video.id}
              id={video.id}
              source={video.source || "Unknown"}
              duration={video.duration}
              url={video.url || ""}
              thumbnailUrl={video.thumbnail_url}
              createdAt={video.created_at}
              metadata={video.metadata}
              onEdit={() => {}}
              onDelete={openDeleteDialog}
            />
          ))}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this video. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteVideo} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default VideosTab;
